import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { credentialsApi } from '../services/api';
import { PLATFORM_CONFIG, PLATFORM_ICONS } from './post/PLATFORM_CONFIG';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { PLATFORM_FEATURES } from '../constants/features';

// ─── Platform credential field definitions ───────────────────────────────────

const PLATFORM_FIELDS = {
  youtube: {
    note: 'YouTube uses OAuth 2.0. Enter your OAuth Client ID, Secret, and Channel ID. Then click the Connect button below to authorize the app. The Channel ID is required for comment automation (find it in YouTube Studio → Settings → Channel → Basic Info).',
    fields: [
      { key: 'client_id', label: 'OAuth Client ID', secret: false },
      { key: 'client_secret', label: 'OAuth Client Secret', secret: true },
      { key: 'channel_id', label: 'Channel ID', secret: false, placeholder: 'UCxxxxxxxxxxxxxxxxxxxx' },
    ],
  },
  instagram: {
    note: 'Instagram Graph API requires a long-lived access token from a Facebook Developer App connected to your Instagram account.',
    fields: [
      { key: 'access_token', label: 'Access Token', secret: true },
    ],
  },
  facebook: {
    note: 'Requires a Page Access Token and your Facebook Page ID. Get both from the Facebook Developer Console.',
    fields: [
      { key: 'page_access_token', label: 'Page Access Token', secret: true },
      { key: 'page_id', label: 'Page ID', secret: false },
    ],
  },
  twitter: {
    note: 'Requires a Twitter Developer App with OAuth 1.0a User Context permissions. All five credentials are required.',
    fields: [
      { key: 'bearer_token', label: 'Bearer Token', secret: true },
      { key: 'api_key', label: 'API Key (Consumer Key)', secret: false },
      { key: 'api_secret', label: 'API Key Secret', secret: true },
      { key: 'access_token', label: 'Access Token', secret: false },
      { key: 'access_token_secret', label: 'Access Token Secret', secret: true },
    ],
  },
  linkedin: {
    note: 'Requires a LinkedIn OAuth 2.0 access token with r_liteprofile and w_member_social scopes.',
    fields: [
      { key: 'access_token', label: 'Access Token', secret: true },
    ],
  },
  reddit: {
    note: 'Create a "script" type app at reddit.com/prefs/apps. Use your Reddit account username and password for the script authentication.',
    fields: [
      { key: 'client_id', label: 'Client ID', secret: false },
      { key: 'client_secret', label: 'Client Secret', secret: true },
      { key: 'username', label: 'Reddit Username', secret: false },
      { key: 'password', label: 'Reddit Password', secret: true },
      { key: 'user_agent', label: 'User Agent', secret: false, placeholder: 'MyApp/1.0 by u/myusername' },
    ],
  },
};

// ─── Single platform section ──────────────────────────────────────────────────

function PlatformSection({ platform, savedCreds, onSave }) {
  const config = PLATFORM_CONFIG[platform];
  const icon = PLATFORM_ICONS[platform];
  const fieldDefs = PLATFORM_FIELDS[platform];

  const [values, setValues] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Populate from saved creds on mount / when savedCreds changes
  useEffect(() => {
    if (savedCreds) {
      const populated = {};
      fieldDefs.fields.forEach(({ key }) => {
        populated[key] = savedCreds[key] || '';
      });
      setValues(populated);
    }
  }, [savedCreds, fieldDefs.fields]);

  const hasAnySaved = savedCreds && Object.values(savedCreds).some(Boolean);
  const allFieldsFilled = fieldDefs.fields.every(({ key }) => values[key]?.trim());

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(platform, values);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(`Are you sure you want to clear credentials for ${config.label}?`)) return;
    setSaving(true);
    try {
      await credentialsApi.delete(platform);
      setValues({});
      setSaved(false);
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectYoutube = async () => {
    try {
      const data = await credentialsApi.getYouTubeAuthUrl();
      if (data && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data?.error || 'Failed to initialize YouTube auth');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
  };

  return (
    <Accordion
      sx={{
        mb: 2,
        borderRadius: '16px !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid',
        borderColor: hasAnySaved ? alpha(config.color, 0.3) : 'divider',
        background: 'rgba(255,255,255,0.02)',
        '&:hover': {
          borderColor: alpha(config.color, 0.5),
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: hasAnySaved ? alpha(config.color, 0.04) : 'transparent',
          borderRadius: '16px',
          minHeight: 64,
          px: 3,
          '&.Mui-expanded': { borderRadius: '16px 16px 0 0' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1 }}>
          <Avatar sx={{ bgcolor: alpha(config.color, 0.1), color: config.color, width: 40, height: 40, border: `1px solid ${alpha(config.color, 0.2)}` }}>
            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {config.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {fieldDefs.fields.length} parameter{fieldDefs.fields.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ mr: 1 }}>
            {hasAnySaved ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                label="Secured"
                size="small"
                sx={{
                  background: alpha(config.color, 0.12),
                  color: config.color,
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { color: config.color },
                }}
              />
            ) : (
              <Chip
                label="Unconfigured"
                size="small"
                sx={{ color: 'text.disabled', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 4 }}>
        <Alert
          severity="info"
          icon={<InfoIcon fontSize="small" />}
          sx={{ mb: 4, borderRadius: 3, fontSize: '0.85rem', bgcolor: alpha('#6366f1', 0.05), color: '#4338ca', border: '1px solid rgba(99, 102, 241, 0.1)' }}
        >
          {fieldDefs.note}
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {fieldDefs.fields.map(({ key, label, secret, placeholder }) => (
            <TextField
              key={key}
              fullWidth
              label={label}
              placeholder={placeholder || (secret ? '••••••••••••' : `Enter ${label}`)}
              value={values[key] || ''}
              onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
              type={secret && !showSecret[key] ? 'password' : 'text'}
              InputProps={secret ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowSecret((prev) => ({ ...prev, [key]: !prev[key] }))}
                      edge="end"
                    >
                      {showSecret[key] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 3 }
              }}
            />
          ))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>{error}</Alert>
        )}

        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            data-testid="save-credentials-button"
            disabled={saving || !allFieldsFilled}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : saved ? <CheckCircleIcon /> : <SaveIcon />}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.25,
              fontWeight: 800,
              background: saved
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : `linear-gradient(135deg, ${config.color}, ${alpha(config.color, 0.8)})`,
              boxShadow: `0 4px 14px ${alpha(config.color, 0.35)}`,
            }}
          >
            {saving ? 'Syncing...' : saved ? 'Success' : 'Secure Credentials'}
          </Button>

          {hasAnySaved && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleClear}
              data-testid="reset-credentials-button"
              disabled={saving}
              startIcon={<DeleteIcon />}
              sx={{ borderRadius: 3, fontWeight: 800, px: 3 }}
            >
              Reset
            </Button>
          )}
        </Box>

        {platform === 'youtube' && hasAnySaved && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
              OAuth Connection
            </Typography>
            {savedCreds?.refresh_token ? (
              <Alert severity="success" sx={{ borderRadius: 3, alignItems: 'center' }}>
                Successfully connected to YouTube!
              </Alert>
            ) : (
              <Button
                variant="contained"
                onClick={handleConnectYoutube}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.25,
                  fontWeight: 800,
                  bgcolor: '#ff0000',
                  '&:hover': { bgcolor: '#cc0000' }
                }}
              >
                Connect to YouTube
              </Button>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Main Settings page ───────────────────────────────────────────────────────

const Settings = React.memo(() => {
  const [credentials, setCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState('');
  const { getAllowedPlatforms } = useFeatureToggles();

  // Show a platform in Settings if it's enabled in ANY platform feature
  const allowedPlatforms = [...new Set(
    PLATFORM_FEATURES.flatMap(feat => getAllowedPlatforms(feat))
  )];
  const visiblePlatforms = Object.keys(PLATFORM_FIELDS).filter(p => allowedPlatforms.includes(p));

  useEffect(() => {
    (async () => {
      try {
        const creds = await credentialsApi.getAll();
        setCredentials(creds || {});

        // Handle YouTube OAuth Callback
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
          // Clear the code synchronously to prevent React Strict Mode from double-firing the one-time code
          window.history.replaceState({}, document.title, window.location.pathname);

          setLoading(true);
          try {
            const data = await credentialsApi.handleYouTubeCallback(code);
            if (data && data.success) {
              // Reload credentials
              const updatedCreds = await credentialsApi.getAll();
              setCredentials(updatedCreds || {});
            } else {
              setGlobalError(data?.error || 'Failed to connect YouTube');
            }
          } catch (callbackErr) {
            setGlobalError(callbackErr.message || 'Failed to handle YouTube callback');
          }
        }
      } catch (err) {
        setGlobalError('Could not load saved credentials. Verify Firebase connectivity.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = useCallback(async (platform, values) => {
    await credentialsApi.save(platform, values);
    setCredentials((prev) => ({ ...prev, [platform]: values }));
  }, []);

  const configuredCount = visiblePlatforms.filter(
    (p) => credentials[p] && Object.values(credentials[p]).some(Boolean)
  ).length;

  return (
    <Box>
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
            Platform Settings
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Connect your social empire. Your credentials are encrypted and stored securely.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip
            label={`${configuredCount} / ${visiblePlatforms.length} Linked`}
            sx={{ fontWeight: 900, px: 1, height: 40, borderRadius: 3, background: alpha('#6366f1', 0.1), color: '#6366f1' }}
          />
        </Box>
      </Box>

      {globalError && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 4 }}>{globalError}</Alert>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 5,
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(30px)',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 20px 50px -20px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 3 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.secondary' }}>Loading Safely...</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>Vault Security</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.7, maxWidth: 700 }}>
                    We use Firebase Firestore with strict security rules to protect your API keys.
                    The backend only accesses these values during post execution via secure server-side calls.
                    Never share your Client Secrets or Access Tokens.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 5, opacity: 0.5 }} />

                <Box sx={{ maxWidth: 900 }}>
                  {visiblePlatforms.map((platform) => (
                    <PlatformSection
                      key={platform}
                      platform={platform}
                      savedCreds={credentials[platform]}
                      onSave={handleSave}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default Settings;
